/**
 * Company Service
 * API integration for company CRUD operations and sub-resources
 */

import apiClient from './apiClient';
import {
  Company,
  CreateCompanyRequest,
  UpdateCompanyRequest,
  CompanyFilters,
  CompanyResponsible,
  CompanyProviderLink,
  CompanyContact,
  CompanyCnae,
  CompanyDashboard,
  CompanyHierarchy,
  CompanySummary,
  ResponsibleType,
  ESocialSendMode,
} from '../types/company';
import { PaginatedResponse } from '../types/common';

export interface CompanyListParams {
  page?: number;
  limit?: number;
  search?: string;
  filters?: Partial<CompanyFilters>;
  /** Accepted by the API: displayName, legalName, tradeName, cnpj, createdAt, updatedAt, id */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ==================== COMPANY MAIN METHODS ====================

class CompanyService {
  private baseUrl = '/companies';

  /**
   * Get paginated list of companies
   */
  async getCompanies(params: CompanyListParams = {}): Promise<PaginatedResponse<Company>> {
    const { page = 1, limit = 10, search, filters, sortBy, sortOrder } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      queryParams.append('search', search);
    }

    if (sortBy) {
      queryParams.append('sortBy', sortBy);
    }

    if (sortOrder) {
      queryParams.append('sortOrder', sortOrder);
    }

    if (filters) {
      // Modules filter - send as comma-separated array
      if (filters.modules && filters.modules.length > 0) {
        queryParams.append('modules', filters.modules.join(','));
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.companyType) {
        queryParams.append('companyType', filters.companyType);
      }
      if (filters.state) {
        queryParams.append('state', filters.state);
      }
      if (filters.city) {
        queryParams.append('city', filters.city);
      }
      if (filters.esocialSendMode) {
        queryParams.append('esocialSendMode', filters.esocialSendMode);
      }
      if (filters.parentCompanyId) {
        queryParams.append('parentCompanyId', filters.parentCompanyId.toString());
      }
      if (filters.includeDeleted) {
        queryParams.append('includeDeleted', 'true');
      }
    }

    const response = await apiClient.get(`${this.baseUrl}?${queryParams}`);

    // Backend retorna: { message: string, data: { companies: Company[], total: number } }
    // response já é ApiResponse, então response.data é { companies, total }
    const backendData = response.data;
    return {
      data: backendData.companies ?? [],
      pagination: {
        total: backendData.total ?? 0,
        page: page,
        limit: limit,
        totalPages: Math.ceil((backendData.total ?? 0) / limit)
      }
    } as PaginatedResponse<Company>;
  }

  /**
   * Get company by ID
   */
  async getCompanyById(id: string, includeRelations: boolean = false): Promise<Company> {
    const url = includeRelations
      ? `${this.baseUrl}/${id}?include=relations`
      : `${this.baseUrl}/${id}`;
    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Create new company
   */
  async createCompany(data: CreateCompanyRequest): Promise<Company> {
    console.log('Creating company:', data);

    const response = await apiClient.post(this.baseUrl, data);

    console.log('Company created successfully:', response.data);
    return response.data;
  }

  /**
   * Update existing company
   */
  async updateCompany(id: string, data: UpdateCompanyRequest): Promise<Company> {
    console.log('Updating company:', { id, data });

    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);

    console.log('Company updated successfully:', response.data);
    return response.data;
  }

  /**
   * Delete company (soft delete)
   */
  async deleteCompany(id: string): Promise<void> {
    console.log('Deleting company:', id);

    await apiClient.delete(`${this.baseUrl}/${id}`);

    console.log('Company deleted successfully');
  }

  /**
   * Toggle company active status
   */
  async toggleCompanyStatus(id: string): Promise<Company> {
    console.log('Toggling company status:', id);

    const response = await apiClient.patch(`${this.baseUrl}/${id}/toggle-status`);

    console.log('Company status toggled:', response.data);
    return response.data;
  }

  /**
   * Validate CNPJ
   */
  async validateCnpj(cnpj: string): Promise<{ valid: boolean; message?: string }> {
    const response = await apiClient.post(`${this.baseUrl}/validate-cnpj`, { cnpj });
    return response.data;
  }

  /**
   * Lookup company data by CNPJ (external API - Receita Federal)
   */
  async lookupCNPJ(cnpj: string): Promise<{
    legalName: string;
    tradeName?: string;
    cnpj: string;
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    situation?: string;
    openingDate?: string;
    companyType?: string;
    email?: string;
    phone?: string;
    cnaeMain?: { code: string; description: string };
    cnaeSecondary?: Array<{ code: string; description: string }>;
  }> {
    console.log('Looking up CNPJ via API:', cnpj.replace(/\d/g, '*'));

    const response = await apiClient.get(`${this.baseUrl}/lookup/${cnpj}`);

    console.log('CNPJ lookup successful:', response.data);
    return response.data;
  }

  /**
   * Lookup CNAE risk levels from reference table (NR-4)
   */
  async lookupCnaeRiskLevels(codes: string[]): Promise<Array<{
    code: string;
    description: string;
    riskLevel: number;
  }>> {
    const response = await apiClient.get(`/cnae-reference/lookup`, {
      params: { codes: codes.join(',') },
    });
    return response.data;
  }

  /**
   * Get company statistics
   */
  async getCompanyStats(): Promise<{
    total: number;
    active: number;
    inactive: number;
    byType: Record<string, number>;
    byModule: Record<string, number>;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/stats`);
    return response.data;
  }

  /**
   * Get companies by parent (branches of a headquarters)
   */
  async getCompaniesByParent(parentId: string): Promise<Company[]> {
    const response = await apiClient.get(`${this.baseUrl}/by-parent/${parentId}`);
    return response.data;
  }

  /**
   * Get company dashboard with statistics
   */
  async getCompanyDashboard(id: string): Promise<CompanyDashboard> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/dashboard`);
    return response.data;
  }

  /**
   * Get company hierarchy (parent and branches)
   */
  async getCompanyHierarchy(id: string): Promise<CompanyHierarchy> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/hierarchy`);
    return response.data;
  }

  /**
   * Get company summary (responsibles, contacts, CNAEs) for expandable rows
   */
  async getSummary(id: string): Promise<CompanySummary> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/summary`);
    return response.data;
  }

  /**
   * Configure eSocial settings for company
   */
  async updateEsocialConfig(
    id: string,
    config: { esocialSendMode: ESocialSendMode; ecacExpirationDate?: string }
  ): Promise<Company> {
    console.log('Updating eSocial config:', { id, config });

    const response = await apiClient.put(`${this.baseUrl}/${id}/esocial/config`, config);

    console.log('eSocial config updated:', response.data);
    return response.data;
  }

  /**
   * Restore a soft-deleted company
   */
  async restoreCompany(id: string): Promise<Company> {
    console.log('Restoring company:', id);

    const response = await apiClient.post(`${this.baseUrl}/${id}/restore`);

    console.log('Company restored:', response.data);
    return response.data;
  }

  /**
   * Permanently delete a company (hard delete)
   */
  async permanentDelete(id: string): Promise<void> {
    console.log('Permanently deleting company:', id);

    await apiClient.delete(`${this.baseUrl}/${id}/permanent`);

    console.log('Company permanently deleted');
  }

  /**
   * Activate company
   */
  async activateCompany(id: string): Promise<Company> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/activate`);
    return response.data;
  }

  /**
   * Suspend company
   */
  async suspendCompany(id: string): Promise<Company> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/suspend`);
    return response.data;
  }

  // ==================== DROPDOWN ====================

  /**
   * Get companies for dropdown (simplified list: id + name)
   */
  async getCompaniesForDropdown(): Promise<{ id: number; name: string }[]> {
    const response = await apiClient.get(`${this.baseUrl}/dropdown`);
    return response.data;
  }

  // ==================== EXPORT METHODS ====================

  /**
   * Export companies to Excel (.xlsx)
   */
  async exportCompanies(filters?: Partial<CompanyFilters>): Promise<Blob> {
    const queryParams = new URLSearchParams();

    if (filters) {
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.state) queryParams.append('state', filters.state);
      if (filters.city) queryParams.append('city', filters.city);
      if (filters.companyType) queryParams.append('companyType', filters.companyType);
      if (filters.modules && filters.modules.length > 0) {
        queryParams.append('modules', filters.modules.join(','));
      }
      if (filters.esocialSendMode) queryParams.append('esocialSendMode', filters.esocialSendMode);
    }

    const response = await apiClient.get(`${this.baseUrl}/export?${queryParams}`, {
      responseType: 'blob',
    });

    if (response instanceof Blob) {
      return response;
    }
    return new Blob([response as unknown as BlobPart]);
  }

  // ==================== PROVIDER LINKS METHODS ====================

  /**
   * Get all provider links for a company
   */
  async getLinkedProviders(
    companyId: string,
    filters?: { activeOnly?: boolean }
  ): Promise<CompanyProviderLink[]> {
    const queryParams = new URLSearchParams();
    if (filters?.activeOnly !== undefined) {
      queryParams.append('activeOnly', filters.activeOnly.toString());
    }

    const url = queryParams.toString()
      ? `${this.baseUrl}/${companyId}/providers?${queryParams}`
      : `${this.baseUrl}/${companyId}/providers`;

    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Link a provider to a company
   */
  async linkProvider(
    companyId: string,
    data: { providerId: number; startDate: string; endDate?: string; notes?: string }
  ): Promise<CompanyProviderLink> {
    const response = await apiClient.post(`${this.baseUrl}/${companyId}/providers`, data);
    return response.data;
  }

  /**
   * Update a provider link
   */
  async updateProviderLink(
    companyId: string,
    linkId: string,
    data: { startDate?: string; endDate?: string | null; notes?: string }
  ): Promise<CompanyProviderLink> {
    const response = await apiClient.put(
      `${this.baseUrl}/${companyId}/providers/${linkId}`,
      data
    );
    return response.data;
  }

  /**
   * Remove a provider link
   */
  async removeProviderLink(companyId: string, linkId: string): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${companyId}/providers/${linkId}`);
  }

  /**
   * End a provider link
   */
  async endProviderLink(
    companyId: string,
    linkId: string,
    endDate?: string
  ): Promise<CompanyProviderLink> {
    const response = await apiClient.post(
      `${this.baseUrl}/${companyId}/providers/${linkId}/end`,
      { endDate }
    );
    return response.data;
  }

  // ==================== RESPONSIBLES METHODS ====================

  /**
   * Get all responsibles for a company
   */
  async getResponsibles(
    companyId: string,
    filters?: { type?: ResponsibleType; activeOnly?: boolean }
  ): Promise<CompanyResponsible[]> {
    const queryParams = new URLSearchParams();

    if (filters?.type) {
      queryParams.append('type', filters.type);
    }
    if (filters?.activeOnly !== undefined) {
      queryParams.append('activeOnly', filters.activeOnly.toString());
    }

    const url = queryParams.toString()
      ? `${this.baseUrl}/${companyId}/responsibles?${queryParams}`
      : `${this.baseUrl}/${companyId}/responsibles`;

    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Get a responsible by ID
   */
  async getResponsibleById(companyId: string, responsibleId: string): Promise<CompanyResponsible> {
    const response = await apiClient.get(
      `${this.baseUrl}/${companyId}/responsibles/${responsibleId}`
    );
    return response.data;
  }

  /**
   * Create a new responsible
   */
  async createResponsible(
    companyId: string,
    data: Omit<CompanyResponsible, 'id' | 'companyId' | 'createdAt' | 'updatedAt' | 'provider' | 'professional'>
  ): Promise<CompanyResponsible> {
    console.log('Creating responsible:', { companyId, data });

    const response = await apiClient.post(`${this.baseUrl}/${companyId}/responsibles`, data);

    console.log('Responsible created:', response.data);
    return response.data;
  }

  /**
   * Update a responsible
   */
  async updateResponsible(
    companyId: string,
    responsibleId: string,
    data: Partial<Omit<CompanyResponsible, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>
  ): Promise<CompanyResponsible> {
    console.log('Updating responsible:', { companyId, responsibleId, data });

    const response = await apiClient.put(
      `${this.baseUrl}/${companyId}/responsibles/${responsibleId}`,
      data
    );

    console.log('Responsible updated:', response.data);
    return response.data;
  }

  /**
   * Delete a responsible
   */
  async deleteResponsible(companyId: string, responsibleId: string): Promise<void> {
    console.log('Deleting responsible:', { companyId, responsibleId });

    await apiClient.delete(`${this.baseUrl}/${companyId}/responsibles/${responsibleId}`);

    console.log('Responsible deleted');
  }

  /**
   * End a responsibility (set endDate)
   */
  async endResponsibility(
    companyId: string,
    responsibleId: string,
    endDate?: string
  ): Promise<CompanyResponsible> {
    console.log('Ending responsibility:', { companyId, responsibleId, endDate });

    const response = await apiClient.post(
      `${this.baseUrl}/${companyId}/responsibles/${responsibleId}/end`,
      { endDate }
    );

    console.log('Responsibility ended:', response.data);
    return response.data;
  }

  // ==================== CONTACTS METHODS ====================

  /**
   * Get all contacts for a company
   */
  async getContacts(companyId: string, activeOnly?: boolean): Promise<CompanyContact[]> {
    const url = activeOnly !== undefined
      ? `${this.baseUrl}/${companyId}/contacts?activeOnly=${activeOnly}`
      : `${this.baseUrl}/${companyId}/contacts`;

    const response = await apiClient.get(url);
    return response.data;
  }

  /**
   * Get a contact by ID
   */
  async getContactById(companyId: string, contactId: string): Promise<CompanyContact> {
    const response = await apiClient.get(`${this.baseUrl}/${companyId}/contacts/${contactId}`);
    return response.data;
  }

  /**
   * Create a new contact
   */
  async createContact(
    companyId: string,
    data: Omit<CompanyContact, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>
  ): Promise<CompanyContact> {
    console.log('Creating contact:', { companyId, data });

    const response = await apiClient.post(`${this.baseUrl}/${companyId}/contacts`, data);

    console.log('Contact created:', response.data);
    return response.data;
  }

  /**
   * Update a contact
   */
  async updateContact(
    companyId: string,
    contactId: string,
    data: Partial<Omit<CompanyContact, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>
  ): Promise<CompanyContact> {
    console.log('Updating contact:', { companyId, contactId, data });

    const response = await apiClient.put(
      `${this.baseUrl}/${companyId}/contacts/${contactId}`,
      data
    );

    console.log('Contact updated:', response.data);
    return response.data;
  }

  /**
   * Delete a contact
   */
  async deleteContact(companyId: string, contactId: string): Promise<void> {
    console.log('Deleting contact:', { companyId, contactId });

    await apiClient.delete(`${this.baseUrl}/${companyId}/contacts/${contactId}`);

    console.log('Contact deleted');
  }

  // ==================== CNAES METHODS ====================

  /**
   * Get all CNAEs for a company
   */
  async getCnaes(companyId: string): Promise<CompanyCnae[]> {
    const response = await apiClient.get(`${this.baseUrl}/${companyId}/cnaes`);
    return response.data;
  }

  /**
   * Get the primary CNAE for a company
   */
  async getPrimaryCnae(companyId: string): Promise<CompanyCnae | null> {
    const response = await apiClient.get(`${this.baseUrl}/${companyId}/cnaes/primary`);
    return response.data;
  }

  /**
   * Get a CNAE by ID
   */
  async getCnaeById(companyId: string, cnaeId: string): Promise<CompanyCnae> {
    const response = await apiClient.get(`${this.baseUrl}/${companyId}/cnaes/${cnaeId}`);
    return response.data;
  }

  /**
   * Create a new CNAE
   */
  async createCnae(
    companyId: string,
    data: Omit<CompanyCnae, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>
  ): Promise<CompanyCnae> {
    console.log('Creating CNAE:', { companyId, data });

    const response = await apiClient.post(`${this.baseUrl}/${companyId}/cnaes`, data);

    console.log('CNAE created:', response.data);
    return response.data;
  }

  /**
   * Update a CNAE
   */
  async updateCnae(
    companyId: string,
    cnaeId: string,
    data: Partial<Omit<CompanyCnae, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>
  ): Promise<CompanyCnae> {
    console.log('Updating CNAE:', { companyId, cnaeId, data });

    const response = await apiClient.put(`${this.baseUrl}/${companyId}/cnaes/${cnaeId}`, data);

    console.log('CNAE updated:', response.data);
    return response.data;
  }

  /**
   * Delete a CNAE
   */
  async deleteCnae(companyId: string, cnaeId: string): Promise<void> {
    console.log('Deleting CNAE:', { companyId, cnaeId });

    await apiClient.delete(`${this.baseUrl}/${companyId}/cnaes/${cnaeId}`);

    console.log('CNAE deleted');
  }

  /**
   * Bulk create CNAEs
   */
  async bulkCreateCnaes(
    companyId: string,
    cnaes: Array<Omit<CompanyCnae, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>>
  ): Promise<CompanyCnae[]> {
    console.log('Bulk creating CNAEs:', { companyId, count: cnaes.length });

    const response = await apiClient.post(`${this.baseUrl}/${companyId}/cnaes/bulk`, { cnaes });

    console.log('CNAEs created:', response.data);
    return response.data;
  }

  /**
   * Set a CNAE as primary
   */
  async setCnaePrimary(companyId: string, cnaeId: string): Promise<CompanyCnae> {
    console.log('Setting CNAE as primary:', { companyId, cnaeId });

    const response = await apiClient.put(
      `${this.baseUrl}/${companyId}/cnaes/${cnaeId}/set-primary`
    );

    console.log('CNAE set as primary:', response.data);
    return response.data;
  }
}

export const companyService = new CompanyService();
export default companyService;
